import { QueryClient } from "@tanstack/react-query"
import type { DateInputValue } from "../setup/components/DateInput"
import { HardwareDevices, IShiftPlanDoc } from "../typings"
import {
  AddressComponent,
  OfficeOption,
} from "../setup/components/SetupWorkplaceSelection"
import { IState } from "pages/dashboard/people/people/typings"
import {
  ConfigFormValues,
  ConfigInitialValues,
  ExtendedShiftPlanDoc,
} from "../setup/components/SetupShiftPlans"

export const toDateFromInput = (
  value: DateInputValue | undefined
): Date | null => {
  if (!value?.dd || !value?.mm || !value?.yyyy) return null
  const d = new Date(`${value.yyyy}-${value.mm}-${value.dd}`)
  if (Number.isNaN(d.getTime())) return null
  return d
}

/** True when `value` is a valid date earlier than today (time of day ignored). */
export const isBeforeToday = (value?: DateInputValue): boolean => {
  const d = toDateFromInput(value)
  if (!d) return false
  const now = new Date()
  return (
    d.getTime() < Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
  )
}

/** True when both inputs point at the same calendar day (digit padding ignored). */
export const isSameDate = (
  a?: DateInputValue,
  b?: DateInputValue
): boolean =>
  Number(a?.dd) === Number(b?.dd) &&
  Number(a?.mm) === Number(b?.mm) &&
  Number(a?.yyyy) === Number(b?.yyyy)

/** True when every part is fully typed (DD, MM, YYYY). */
export const isCompleteDate = (value?: DateInputValue) =>
  value?.dd?.length === 2 &&
  value?.mm?.length === 2 &&
  value?.yyyy?.length === 4

export const WEEK_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const

export type DayOfWeek = (typeof WEEK_DAYS)[number]

const MS_PER_DAY = 86_400_000

/**
 * Dates are compared in UTC because `toDateFromInput` / `toIsoDateString` treat
 * a DateInputValue as a UTC calendar date. Using local getters here would shift
 * the weekday by one in negative-offset timezones.
 */
const toUtcDate = (value: DateInputValue | undefined): Date | null => {
  const dd = Number(value?.dd)
  const mm = Number(value?.mm)
  const yyyy = Number(value?.yyyy)
  if (!dd || !mm || !yyyy || String(value?.yyyy).length !== 4) return null
  const d = new Date(Date.UTC(yyyy, mm - 1, dd))
  // Rejects overflow like 31/02, which Date.UTC would roll into March.
  if (
    d.getUTCFullYear() !== yyyy ||
    d.getUTCMonth() !== mm - 1 ||
    d.getUTCDate() !== dd
  )
    return null
  return d
}

/**
 * The weekdays a shift table's date range actually covers — a table running
 * Tue→Thu covers only Tuesday, Wednesday and Thursday, so every other day must
 * be disabled in the roster grid.
 *
 * Falls back to the full week while the range is incomplete or invalid (the
 * admin is still typing a date), so an unset range never disables everything.
 */
export function getDaysInDateRange(
  startDate: DateInputValue | undefined,
  endDate: DateInputValue | undefined
): Set<DayOfWeek> {
  const start = toUtcDate(startDate)
  const end = toUtcDate(endDate)
  if (!start || !end || end.getTime() < start.getTime())
    return new Set(WEEK_DAYS)

  const spanInDays = Math.round((end.getTime() - start.getTime()) / MS_PER_DAY)
  if (spanInDays >= 6) return new Set(WEEK_DAYS)

  const days = new Set<DayOfWeek>()
  for (let i = 0; i <= spanInDays; i++) {
    const day = new Date(start.getTime() + i * MS_PER_DAY)
    // getUTCDay() is Sunday-based; the grid is Monday-based.
    days.add(WEEK_DAYS[(day.getUTCDay() + 6) % 7])
  }
  return days
}

export const toIsoDateString = (
  value: DateInputValue | undefined
): string | null => {
  const d = toDateFromInput(value)
  return d ? d.toISOString().slice(0, 10) : null
}

/** Parse API date "YYYY-MM-DD" to DateInputValue and label */
export function parseApiDate(apiDate: string): {
  value: DateInputValue
  label: string
} {
  const d = new Date(apiDate)
  if (Number.isNaN(d.getTime()))
    return { value: { dd: "", mm: "", yyyy: "" }, label: "" }
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  const yyyy = String(y)
  return {
    value: { dd: day, mm: m, yyyy },
    label: `${day}/${m}/${yyyy}`,
  }
}

export const to12HourLabel = (time: string): string => {
  if (!time) return ""
  const [rawHour = "0", rawMinute = "0"] = time.split(":")
  let hourNum = parseInt(rawHour || "0", 10)
  if (Number.isNaN(hourNum)) hourNum = 0
  const meridiem = hourNum >= 12 ? "PM" : "AM"
  let displayHour = hourNum % 12
  if (displayHour === 0) displayHour = 12
  const minute = String(parseInt(rawMinute || "0", 10)).padStart(2, "0")
  return `${String(displayHour).padStart(2, "0")}:${minute} ${meridiem}`
}

export const calculateRadius = (lat: number, lng: number) => {
  return 500 + Math.abs(lat % 10) * 10 + Math.abs(lng % 10) * 10
}

// utils/shiftPlanUtils.ts

const BUFFER_WHITELIST = new Set([15, 30, 60])

export function bufferMinutes(value: string | undefined): number {
  const parsed = Number(value)
  return BUFFER_WHITELIST.has(parsed) ? parsed : 20
}

export function shiftPlanToConfigInitialValues(plan: IShiftPlanDoc) {
  return {
    allowManualTimeEntry: plan.allowManualTimeEntry ?? false,
    requireApprovalForManualTimeEntry:
      plan.requireApprovalForManualTimeEntry ?? false,
    useInPayrollCalculation: plan.useInPayrollCalculation ?? false,
    allowSwapping: plan.allowSwapping ?? false,
    requireApprovalForSwap: plan.requireApprovalForSwap ?? false,
    allowLateClockIn: plan.allowLateClockIn ?? false,
    allowedLateClockinInMinutes: plan.allowedLateClockinInMinutes
      ? String(plan.allowedLateClockinInMinutes)
      : undefined,
    allowOvertime: plan.allowOvertime ?? false,
    useOvertimeRecord: plan.useOvertimeRecord ?? false,
    linkHardwareDevice: plan.linkHardwareDevice ?? false,
    hardwareDeviceTypes: plan.hardwareDeviceTypes
      ? (plan.hardwareDeviceTypes.map((d: string | HardwareDevices) =>
          typeof d === "string" ? d : d.deviceType
        ) as (string | HardwareDevices)[])
      : [],
    hardwareSetupContactEmail: plan.hardwareSetupContactEmail ?? "",
    allowGeoFencing: plan.allowGeoFencing ?? false,
    geofencingOffsetMeters: plan.geofencingOffsetMeters ?? 0,
    addOvertimeToPayroll: plan.addOvertimeToPayroll ?? false,
    smsNotification: plan.smsNotification ?? false,
    notificationCadence: plan.notificationCadence ?? undefined,
    notifiyMeBefore: plan.notifiyMeBefore ?? false,
  }
}

export function buildShiftPlanBody(
  config: Record<string, unknown>
): Record<string, unknown> {
  return {
    allowManualTimeEntry: !!config.allowManualTimeEntry,
    requireApprovalForManualTimeEntry:
      !!config.requireApprovalForManualTimeEntry,
    useInPayrollCalculation: !!config.useInPayrollCalculation,
    allowSwapping: !!config.allowSwapping,
    requireApprovalForSwap: !!config.requireApprovalForSwap,
    allowLateClockIn: !!config.allowLateClockIn,
    allowedLateClockinInMinutes: Number(
      config.allowedLateClockinInMinutes || 0
    ),
    allowOvertime: !!config.allowOvertime,
    useOvertimeRecord: !!config.useOvertimeRecord,
    linkHardwareDevice: !!config.linkHardwareDevice,
    ...(config.hardwareSetupContactEmail
      ? { hardwareSetupContactEmail: String(config.hardwareSetupContactEmail) }
      : {}),
    hardwareDeviceTypes:
      Array.isArray(config.hardwareDeviceTypes) &&
      config.hardwareDeviceTypes.length > 0
        ? config.hardwareDeviceTypes.map((d: HardwareDevices) =>
            typeof d === "string" ? { deviceType: d } : d
          )
        : [],
    allowGeoFencing: !!config.allowGeoFencing,
    geofencingOffsetMeters: Number(config.geofencingOffsetMeters || 0),
    addOvertimeToPayroll: !!config.addOvertimeToPayroll,
    smsNotification: !!config.smsNotification,
    notificationCadence: config.notificationCadence
      ? String(config.notificationCadence)
      : null,
    notifiyMeBefore: !!config.notifiyMeBefore,
  }
}

// utils/shiftPlanUtils.ts
export function invalidateShiftPlanQueries(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    predicate: (query) =>
      typeof query.queryKey?.[0] === "string" &&
      (query.queryKey[0] as string).includes("/v1/time-attendance/shift-plans"),
  })
}

export function getAddressComponent(
  components: AddressComponent[],
  type: string
): AddressComponent | undefined {
  return components.find((c) => c.types?.includes(type))
}

export function matchState(
  states: IState[],
  stateName: string,
  stateShortName: string
): string {
  const matched = states.find((s) => {
    const desc = String(s.description ?? "").toLowerCase()
    return (
      desc === stateName.toLowerCase() ||
      String(s.code ?? "").toLowerCase() === stateShortName.toLowerCase()
    )
  })
  return matched ? String(matched.code) : stateName
}

export function toSelectOptions(offices: OfficeOption[]) {
  return offices.map(({ description }) => ({
    label: description,
    value: description,
  }))
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function shiftPlanToConfigInitialValuesConfig(
  plan: ExtendedShiftPlanDoc
): ConfigInitialValues {
  return {
    allowManualTimeEntry: plan.allowManualTimeEntry ?? false,
    requireApprovalForManualTimeEntry:
      plan.requireApprovalForManualTimeEntry ?? false,
    useInPayrollCalculation: plan.useInPayrollCalculation ?? false,
    allowSwapping: plan.allowSwapping ?? false,
    requireApprovalForSwap: plan.requireApprovalForSwap ?? false,
    allowLateClockIn: plan.allowLateClockIn ?? false,
    allowedLateClockinInMinutes: plan.allowedLateClockinInMinutes
      ? String(plan.allowedLateClockinInMinutes)
      : undefined,
    allowOvertime: plan.allowOvertime ?? false,
    useOvertimeRecord: plan.useOvertimeRecord ?? false,
    linkHardwareDevice: plan.linkHardwareDevice ?? false,
    hardwareDeviceTypes: plan.hardwareDeviceTypes
      ? (plan.hardwareDeviceTypes.map((d: string | HardwareDevices) =>
          typeof d === "string" ? d : d.deviceType
        ) as (string | HardwareDevices)[])
      : [],
    hardwareSetupContactEmail: plan.hardwareSetupContactEmail ?? "",
    allowGeoFencing: plan.allowGeoFencing ?? false,
    geofencingOffsetMeters: plan.geofencingOffsetMeters ?? 0,
    addOvertimeToPayroll: plan.addOvertimeToPayroll ?? false,
    smsNotification: plan.smsNotification ?? false,
    notificationCadence: plan.notificationCadence ?? undefined,
    notifiyMeBefore: plan.notifiyMeBefore ?? false,
  }
}

export function buildShiftPlanBodyConfig(
  config: ConfigFormValues
): Record<string, unknown> {
  return {
    allowManualTimeEntry: !!config.allowManualTimeEntry,
    requireApprovalForManualTimeEntry:
      !!config.requireApprovalForManualTimeEntry,
    useInPayrollCalculation: !!config.useInPayrollCalculation,
    allowSwapping: !!config.allowSwapping,
    requireApprovalForSwap: !!config.requireApprovalForSwap,
    allowLateClockIn: !!config.allowLateClockIn,
    allowedLateClockinInMinutes: Number(
      config.allowedLateClockinInMinutes || 0
    ),
    allowOvertime: !!config.allowOvertime,
    useOvertimeRecord: !!config.useOvertimeRecord,
    linkHardwareDevice: !!config.linkHardwareDevice,
    hardwareSetupContactEmail: config.hardwareSetupContactEmail
      ? String(config.hardwareSetupContactEmail)
      : "",
    hardwareDeviceTypes:
      config.hardwareDeviceTypes?.length! > 0
        ? config?.hardwareDeviceTypes?.map((d: string | HardwareDevices) =>
            typeof d === "string" ? d : d.deviceType
          )
        : [],
    allowGeoFencing: !!config.allowGeoFencing,
    geofencingOffsetMeters: Number(config.geofencingOffsetMeters || 0),
    addOvertimeToPayroll: !!config.addOvertimeToPayroll,
    smsNotification: !!config.smsNotification,
    notificationCadence: config.notificationCadence
      ? String(config.notificationCadence)
      : null,
    notifiyMeBefore: !!config.notifiyMeBefore,
  }
}
